import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getOvernightStockBars } from "../marketData/marketDataApi";
import "./NinaDayScanner.css";

const REFRESH_MS = 60000;
const REFRESH_DEADLINE_MS = 30000;

const DEFAULT_UNIVERSE = [
  "PLUG", "AAPL", "NVDA", "AMD", "TSLA", "AMZN", "MSFT", "META",
  "GOOGL", "NFLX", "AVGO", "INTC", "SOFI", "HOOD", "COIN", "SMCI",
  "ARM", "PLTR", "RIVN", "LCID", "F", "NIO", "BAC", "JPM", "XOM",
  "CVX", "UBER", "SPY", "QQQ", "IWM", "SLV", "RXT",
];

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function easternClockParts(value) {
  if (!value) return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(parsed);

    const values = Object.fromEntries(
      parts.map((part) => [part.type, part.value])
    );

    const hour = Number(values.hour);
    const minute = Number(values.minute);

    if (
      !Number.isInteger(hour) ||
      !Number.isInteger(minute) ||
      !values.weekday
    ) {
      return null;
    }

    return {
      weekday: values.weekday,
      hour,
      minute,
    };
  } catch (error) {
    return null;
  }
}

function isOvernightTimestamp(value) {
  const parts = easternClockParts(value);
  if (!parts) return false;

  const { weekday, hour, minute } = parts;
  const minutes = hour * 60 + minute;

  if (weekday === "Sat") return false;

  if (weekday === "Sun") {
    return minutes >= 20 * 60;
  }

  if (minutes < 4 * 60) {
    return true;
  }

  if (
    ["Mon", "Tue", "Wed", "Thu"].includes(weekday) &&
    minutes >= 20 * 60
  ) {
    return true;
  }

  return false;
}

function isOvernightSessionActive(now = new Date(Date.now())) {
  return isOvernightTimestamp(now.toISOString());
}

function formatPrice(value) {
  const number = finiteNumber(value);
  return number === null ? "—" : `$${number.toFixed(2)}`;
}

function formatPercent(value) {
  const number = finiteNumber(value);
  if (number === null) return "—";

  return `${number >= 0 ? "+" : ""}${number.toFixed(2)}%`;
}

function formatVolume(value) {
  const number = finiteNumber(value);
  if (number === null) return "—";

  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(number);
}

function formatTimestamp(value) {
  if (!value) return "Update unavailable";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Update unavailable";

  return parsed.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "America/New_York",
    timeZoneName: "short",
  });
}

function currentSelectedSymbol() {
  try {
    return String(
      localStorage.getItem("aninaSelectedSymbol") || "PLUG"
    )
      .trim()
      .toUpperCase();
  } catch (error) {
    return "PLUG";
  }
}

function scannerUniverse() {
  const selected = currentSelectedSymbol();

  return Array.from(
    new Set([selected, ...DEFAULT_UNIVERSE])
  );
}

function normalizeOvernightBars(symbol, bars, source = {}) {
  const overnightBars = (Array.isArray(bars) ? bars : [])
    .filter((bar) => bar?.t && isOvernightTimestamp(bar.t))
    .map((bar) => ({
      ...bar,
      millis: new Date(bar.t).getTime(),
    }))
    .filter((bar) => Number.isFinite(bar.millis))
    .sort((a, b) => a.millis - b.millis);

  if (!overnightBars.length) {
    return null;
  }

  const firstBar = overnightBars[0];
  const latestBar = overnightBars[overnightBars.length - 1];

  const sessionOpen = finiteNumber(firstBar.o);
  const latestPrice = finiteNumber(latestBar.c);

  const dollarChange =
    latestPrice !== null && sessionOpen !== null
      ? latestPrice - sessionOpen
      : null;

  const percentageChange =
    dollarChange !== null && sessionOpen > 0
      ? (dollarChange / sessionOpen) * 100
      : null;

  const highs = overnightBars
    .map((bar) => finiteNumber(bar.h))
    .filter((value) => value !== null);

  const lows = overnightBars
    .map((bar) => finiteNumber(bar.l))
    .filter((value) => value !== null);

  const volume = overnightBars.reduce(
    (total, bar) => total + (finiteNumber(bar.v) ?? 0),
    0
  );

  return {
    symbol,
    latestPrice,
    sessionOpen,
    dollarChange,
    percentageChange,
    high: highs.length ? Math.max(...highs) : null,
    low: lows.length ? Math.min(...lows) : null,
    volume,
    vwap: finiteNumber(latestBar.vw),
    updatedAt: latestBar.t || null,
    barCount: overnightBars.length,
    provider: source.provider || "Alpaca",
    feed: source.feed || "boats",
    quality:
      source.quality_level ||
      source.quality_state ||
      "Overnight limited coverage",
    notice: source.nina_data_quality_notice || "",
  };
}

export default function NinaLiveOvernightScanner({
  onOpenTradeReview,
  refreshDeadlineMs = REFRESH_DEADLINE_MS,
}) {
  const symbols = useMemo(() => scannerUniverse(), []);

  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshWarning, setRefreshWarning] = useState("");
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  const refreshInFlightRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);

  const loadOvernightBars = useCallback(
    async ({ forceRefresh = false } = {}) => {
      if (refreshInFlightRef.current) {
        return false;
      }

      if (!isOvernightSessionActive()) {
        setRows([]);
        setStatus("closed");
        setRefreshWarning("");
        setIsRefreshing(false);
        return false;
      }

      refreshInFlightRef.current = true;

      const isRefresh = hasLoadedOnceRef.current;
      const controller = new AbortController();

      let deadlineReached = false;

      const deadlineId = window.setTimeout(() => {
        deadlineReached = true;
        controller.abort();
      }, refreshDeadlineMs);

      setRefreshWarning("");

      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setStatus("loading");
      }

      try {
        const payload = await getOvernightStockBars(symbols, {
          signal: controller.signal,
          ...(forceRefresh ? { cacheMode: "reload" } : {}),
        });

        if (deadlineReached || controller.signal.aborted) {
          setRefreshWarning(
            "Authenticated overnight refresh timed out. Previous verified rows were kept."
          );

          if (!isRefresh) {
            setStatus("empty");
          }

          return false;
        }

        const barsBySymbol = payload?.bars || {};
const source = payload?.source || {};
const latestBarsBySymbol = payload?.latest?.bars || {};
const latestSource = payload?.latest?.source || {};

const authenticatedRows = symbols
  .map((symbol) => {
    const historyBars = Array.isArray(barsBySymbol[symbol])
      ? barsBySymbol[symbol]
      : [];

    const currentBar = latestBarsBySymbol[symbol] || null;

    const barsForBaseRow =
      historyBars.length > 0
        ? historyBars
        : currentBar
        ? [currentBar]
        : [];

    const baseSource =
      historyBars.length > 0 ? source : latestSource;

    const row = normalizeOvernightBars(
      symbol,
      barsForBaseRow,
      baseSource
    );

    if (
      !row ||
      !currentBar?.t ||
      !isOvernightTimestamp(currentBar.t)
    ) {
      return row;
    }

    const currentPrice = finiteNumber(currentBar.c);

    if (currentPrice === null) {
      return row;
    }

    const dollarChange =
      row.sessionOpen !== null
        ? currentPrice - row.sessionOpen
        : null;

    const percentageChange =
      dollarChange !== null && row.sessionOpen > 0
        ? (dollarChange / row.sessionOpen) * 100
        : null;

    return {
      ...row,
      latestPrice: currentPrice,
      dollarChange,
      percentageChange,
      updatedAt: currentBar.t,
      provider: latestSource.provider || row.provider,
      feed: latestSource.feed || "overnight",
      quality:
        latestSource.quality_level ||
        latestSource.quality_state ||
        row.quality,
      notice:
        latestSource.nina_data_quality_notice ||
        row.notice,
    };
  })
  .filter(Boolean);

        authenticatedRows.sort((a, b) => {
          const aMove = Math.abs(
            finiteNumber(a.percentageChange) ?? -Infinity
          );

          const bMove = Math.abs(
            finiteNumber(b.percentageChange) ?? -Infinity
          );

          return (
            bMove - aMove ||
            a.symbol.localeCompare(b.symbol)
          );
        });

        setRows(authenticatedRows);
        setLastRefreshAt(new Date());
        setStatus(
          authenticatedRows.length ? "ready" : "empty"
        );

        hasLoadedOnceRef.current = true;

        return true;
      } catch (error) {
        setRefreshWarning(
          controller.signal.aborted
            ? "Authenticated overnight refresh timed out. Previous verified rows were kept."
            : "Authenticated overnight refresh failed. Previous verified rows were kept."
        );

        if (!isRefresh) {
          setStatus("empty");
        }

        return false;
      } finally {
        window.clearTimeout(deadlineId);
        refreshInFlightRef.current = false;
        setIsRefreshing(false);
      }
    },
    [refreshDeadlineMs, symbols]
  );

  useEffect(() => {
    let active = true;

    const run = async () => {
      if (active) {
        await loadOvernightBars();
      }
    };

    run();

    const timerId = window.setInterval(
      run,
      REFRESH_MS
    );

    return () => {
      active = false;
      window.clearInterval(timerId);
    };
  }, [loadOvernightBars]);

  const openSymbol = (row) => {
    try {
      localStorage.setItem(
        "aninaSelectedSymbol",
        row.symbol
      );
    } catch (error) {
      // Navigation continues through the events below.
    }

    window.dispatchEvent(
      new CustomEvent("nina:symbol-change", {
        detail: {
          symbol: row.symbol,
        },
      })
    );

    if (typeof onOpenTradeReview === "function") {
      onOpenTradeReview({
        kind: "overnight-live-stock",
        symbol: row.symbol,
        source: "alpaca-live-overnight-scanner",
        evidenceStatus: "provider-live",
        currentPrice: row.latestPrice,
        sessionOpen: row.sessionOpen,
        volume: row.volume,
        vwap: row.vwap,
        updatedAt: row.updatedAt,
        provider: row.provider,
        feed: row.feed,
        dataQuality: row.quality,
        notice: row.notice,
        reviewOnly: true,
      });
    }
  };

  return (
    <section
      className="ninaScannerPage"
      aria-labelledby="live-overnight-scanner-title"
    >
      <header className="scannerHero scannerCompactPanel">
        <span className="scannerEyebrow">
          AUTHENTICATED OVERNIGHT DATA · LIVE
        </span>

        <h1 id="live-overnight-scanner-title">
          🌙 AI Overnight Scanner
        </h1>

       <p>
  Surface current authenticated Alpaca OVERNIGHT data
  with delayed BOATS 5-minute session history from
  8:00 PM to 4:00 AM New York time.
</p>

<div
  className="scannerDemoNotice"
  role="note"
>
  <strong>
    Overnight coverage is limited venue coverage
  </strong>

  <span>
    The AI Assistant uses current authenticated Alpaca OVERNIGHT data
    for the latest price and delayed BOATS bars for session
    history. Overnight data must not be treated as full
    consolidated U.S. market coverage. When the overnight
    session is closed, AninaTrade does not recycle stale
    overnight data as live evidence.
  </span>
</div> 
      </header>

      <section
        className="scannerCompactPanel"
        aria-label="Overnight scanner data status"
      >
        <div className="scannerStatusRow">
          <strong>
            {status === "loading"
              ? "Loading authenticated overnight market data…"
              : isRefreshing
              ? "Refreshing authenticated overnight market data…"
              : status === "closed"
              ? "Overnight session is currently closed"
              : status === "ready"
              ? `${rows.length} authenticated overnight symbols loaded`
              : "No authenticated overnight rows are currently available"}
          </strong>

          <span>
            {lastRefreshAt
              ? `Last refresh ${formatTimestamp(
                  lastRefreshAt.toISOString()
                )}`
              : status === "closed"
              ? "Next overnight session begins at 8:00 PM ET"
              : "Waiting for first refresh"}
          </span>

          <button
            type="button"
            onClick={() =>
              loadOvernightBars({
                forceRefresh: true,
              })
            }
            disabled={
              status === "loading" ||
              status === "closed" ||
              isRefreshing
            }
          >
            {isRefreshing
              ? "Refreshing…"
              : "Refresh now"}
          </button>
        </div>

        {refreshWarning && (
          <div
            className="scannerDemoNotice"
            role="alert"
          >
            <strong>Refresh incomplete</strong>
            <span>{refreshWarning}</span>
          </div>
        )}
      </section>

      <section className="scannerCompactPanel">
        <div className="scannerTableWrap">
          <table className="scannerTable">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Overnight Last</th>
                <th>Session Open</th>
                <th>ON Change</th>
                <th>High</th>
                <th>Low</th>
                <th>Volume</th>
                <th>VWAP</th>
                <th>Bars</th>
                <th>Provider / Feed</th>
                <th>Quality</th>
                <th>Updated ET</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <tr key={row.symbol}>
                  <td>
                    <button
                      type="button"
                      className="scannerSymbolButton"
                      onClick={() => openSymbol(row)}
                      title={`Open ${row.symbol} in Live Trading`}
                    >
                      {row.symbol}
                    </button>
                  </td>

                  <td>
                    {formatPrice(row.latestPrice)}
                  </td>

                  <td>
                    {formatPrice(row.sessionOpen)}
                  </td>

                  <td
                    className={
                      finiteNumber(
                        row.percentageChange
                      ) === null
                        ? ""
                        : row.percentageChange < 0
                        ? "negative"
                        : "positive"
                    }
                  >
                    {formatPercent(
                      row.percentageChange
                    )}
                  </td>

                  <td>
                    {formatPrice(row.high)}
                  </td>

                  <td>
                    {formatPrice(row.low)}
                  </td>

                  <td>
                    {formatVolume(row.volume)}
                  </td>

                  <td>
                    {formatPrice(row.vwap)}
                  </td>

                  <td>
                    {row.barCount}
                  </td>

                  <td>
                    {row.provider}
                    {row.feed
                      ? ` · ${row.feed.toUpperCase()}`
                      : ""}
                  </td>

                  <td>
                    {row.quality}
                  </td>

                  <td>
                    {formatTimestamp(row.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {status === "closed" && (
          <div
            className="scannerDemoNotice"
            role="status"
          >
            <strong>
              Overnight market session is closed.
            </strong>

            <span>
             The AI Assistant will begin requesting authenticated
overnight market data when the eligible
overnight session opens at 8:00 PM ET.
Stale overnight data is not displayed as live. 
            </span>
          </div>
        )}

        {status === "empty" &&
          !isRefreshing && (
            <div
              className="scannerDemoNotice"
              role="status"
            >
              <strong>
                No authenticated overnight rows passed
                the session timestamp check.
              </strong>

              <span>
                AninaTrade leaves this LIVE scanner
                empty rather than substituting
                after-hours, premarket, regular-session,
                or simulated prices.
              </span>
            </div>
          )}
      </section>
    </section>
  );
}